import React, { useCallback, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useLeftNavStore } from './left-nav-store'
import { SlideOutNavItem } from './SlideOutNavItem'
import { SlideOutNavGroup } from './SlideOutNavGroup'
import { resolveChildren, isNavItemEnabled, resolvePath } from './useNavItems'
import { useNavItems } from './NavItemsContext'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { useAuth } from 'context/authentication/useAuth'
import { cn } from 'components/UI/cn'

interface SlideOutPanelProps {
  className?: string
  $isOpen?: boolean
}

export const SlideOutPanel: React.FC<SlideOutPanelProps> = ({
  className,
  $isOpen = false,
}) => {
  const { isSlideOutOpen, activeSection, closeSlideOut, openSlideOut } =
    useLeftNavStore()
  const featureFlags = useFeatureFlagService()
  const { user } = useAuth()
  const { navItems } = useNavItems()
  const location = useLocation()

  // Settings was removed from the left nav (it lives in the top header),
  // so the slide-out panel has no anchor button on the left to re-open it
  // if the user accidentally dismisses it via click-outside or Escape.
  // Treat the panel as part of the page layout on /settings routes: keep
  // it forced open and skip the dismiss handlers entirely.
  const isSettingsRoute = location.pathname.startsWith('/settings')

  const activeItem = useMemo(
    () => navItems.find((item) => item.id === activeSection),
    [navItems, activeSection]
  )

  const title = activeItem?.label ?? ''

  // Settings is always-pinned, so it runs a slimmer section width than the
  // transient section panels. Applied to BOTH the aside and the inner content
  // div so the content shrinks with the panel (edge padding unchanged).
  const panelWidth = isSettingsRoute
    ? 'var(--slide-out-panel-width-settings)'
    : 'var(--slide-out-panel-width)'

  // Auto-open the Settings slide-out when navigating into /settings so the
  // panel shows up even if the user URL-jumped or refreshed on a settings
  // page (state in left-nav-store isn't persisted).
  useEffect(() => {
    if (isSettingsRoute && (!isSlideOutOpen || activeSection !== 'settings')) {
      openSlideOut('settings')
    }
  }, [isSettingsRoute, isSlideOutOpen, activeSection, openSlideOut])

  // Close on escape key — disabled on /settings (see comment above).
  useEffect(() => {
    if (!isSlideOutOpen || isSettingsRoute) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeSlideOut()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isSlideOutOpen, isSettingsRoute, closeSlideOut])

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (
        !target.closest('[data-slide-out-panel]') &&
        !target.closest('[data-left-nav]') &&
        !target.closest('[data-settings-btn]')
      ) {
        closeSlideOut()
      }
    },
    [closeSlideOut]
  )

  useEffect(() => {
    if (!isSlideOutOpen || isSettingsRoute) return

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isSlideOutOpen, isSettingsRoute, handleClickOutside])

  const renderContent = () => {
    if (!activeItem) return null

    if (activeItem.isLoading) {
      return <div className="p-4 text-sm text-fg-secondary">Loading...</div>
    }

    const children = resolveChildren(activeItem)
    if (!children?.length) return null

    return (
      <ul className="list-none m-0 p-0 flex flex-col gap-1.5">
        {children
          .filter((child) => isNavItemEnabled(child, featureFlags, user))
          .map((child) =>
            child.children?.length ? (
              <SlideOutNavGroup
                key={child.id}
                label={child.label}
                description={child.description}
                items={child.children
                  .filter((sub) => isNavItemEnabled(sub, featureFlags, user))
                  .map((sub) => ({
                    label: sub.label,
                    path: resolvePath(sub.path),
                  }))}
                section={activeItem.id}
                defaultExpanded={location.pathname.includes(
                  resolvePath(child.path)
                )}
                isActiveGroup={location.pathname.includes(
                  resolvePath(child.path)
                )}
                keepOpen
              />
            ) : (
              <SlideOutNavItem
                key={resolvePath(child.path)}
                label={child.label}
                icon={child.icon}
                path={resolvePath(child.path)}
                section={activeItem.id}
                minimumRole={child.minimumRole}
                keepOpen
              />
            )
          )}
      </ul>
    )
  }

  return (
    <aside
      style={{
        width: $isOpen ? panelWidth : 0,
        minWidth: $isOpen ? panelWidth : 0,
      }}
      className={cn(
        'bg-[var(--slide-out-bg)] transition-all duration-200 ease-in-out flex flex-col rounded-l-lg overflow-hidden',
        className
      )}
      role="navigation"
      aria-label={`${title} navigation`}
      data-slide-out-panel
    >
      <div className="flex flex-col h-full" style={{ width: panelWidth }}>
        {/* Header */}
        <div className="flex items-center gap-1 p-3 opacity-80">
          <div className="flex items-center justify-center p-1 text-fg [&_svg]:w-6 [&_svg]:h-6">
            {activeItem?.icon}
          </div>
          <h2 className="m-0 flex items-center text-md font-normal leading-[1.5rem] text-fg uppercase tracking-[0.05em]">
            {title}
          </h2>
        </div>

        {/* Divider */}
        <div className="h-px bg-[rgba(25,29,35,0.15)] mx-3 mb-3" />

        {/* Content */}
        <nav className="flex-1 overflow-y-auto px-3 pb-3">
          {renderContent()}
        </nav>
      </div>
    </aside>
  )
}
